import { Route, Routes } from "react-router";
import type { OrgEntity } from "~/core/api";
import { useT } from "~/core/i18n";
import { Empty } from "~/core/ui/states";
import type { FilterState } from "~/modules/analytics/components/filters";
import { CONTENT_PATH, LOOP } from "~/modules/analytics/lib/loop";
import { ContentPage } from "~/modules/analytics/pages/content";
import { OverviewPage } from "~/modules/analytics/pages/overview";
import { StageRoutes } from "~/modules/analytics/pages/stages";

/**
 * Analytics: the five stages of the loop, and the screens that sit outside the
 * numbers because they belong to all five.
 *
 * The module has no path prefix of its own. It is what a brand's workspace is
 * when you have not gone somewhere else, so it occupies the brand root and the
 * Agent takes one segment under it — which is also why stage one's own path,
 * `insights`, reads as the module's entry.
 */
export function AnalyticsRoutes({
	orgId,
	brand,
	filters,
	setFilters,
}: {
	orgId: string;
	brand: OrgEntity;
	filters: FilterState;
	setFilters: (next: Partial<FilterState>) => void;
}) {
	const t = useT();
	const shared = { entityId: brand.id, filters, setFilters };

	return (
		<div className="analytics-workspace">
			<Routes>
				<Route index element={<OverviewPage {...shared} orgId={orgId} />} />
				{/* The content tree, outside the numbers like the overview: every
				    stage adds to it. */}
				<Route path={CONTENT_PATH} element={<ContentPage {...shared} />} />
				{/* One route per stage, from `LOOP`. `/*` because every reading is
			    somewhere the assistant can send a person, somewhere an empty state
			    can link to, and somewhere a bookmark still lands. */}
				{LOOP.map((stage) => (
					<Route key={stage.key} path={`${stage.path}/*`} element={<StageRoutes stage={stage} {...shared} />} />
				))}
				<Route path="*" element={<Empty title={t("errors.not_found")} />} />
			</Routes>
		</div>
	);
}
